import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import LoadingBackdrop from "../../components/LoadingBackdrop.js";
import { useUser } from "../../components/UserContext.js";
import DataBox from "../../components/data-box/DataBox.js";
import ErrorDialog from "../../components/dialogs/ErrorDialog.js";
import { getCharacter, getHost, putHost } from "../../lib/apiConfig.js";
import { PAGE_CONTAINER_SX, PAGE_TITLE_SX } from "../../lib/constants.js";
import { useFetch } from "../../lib/hooks/useFetch.js";
import DiagnosticsButtons from "./components/DiagnosticsButtons.js";
import HostGraph from "./components/HostGraph.js";
import ConfirmationDialog from "./components/dialogs/ConfirmationDialog.js";
import ResultDialog from "./components/dialogs/ResultDialog.js";
import DiagnosticsChecks from "./components/DiagnosticsChecks.js";

const getTasks = ({ locales }) => [
  locales["maintenance.diagnostics.checks.motor"],
  locales["maintenance.diagnostics.checks.perception"],
  locales["maintenance.diagnostics.checks.language"],
  locales["maintenance.diagnostics.checks.emotion"],
];

export default function HostMaintenancePage({ locales }) {
  const { isTrans, runId } = useUser();
  const { state } = useLocation();
  const [hostId, setHostId] = useState(null);
  const [host, setHost] = useState(null);
  const [characterId, setCharacterId] = useState(null);
  const [character, setCharacter] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setError = (error) => {
    setErrorMessage(error.message);
    setShowError(true);
  };

  const { isFetching: isFetchingHost } = useFetch({
    endpoint: getHost({ id: hostId, runId }),
    mustFetch: hostId && !host,
    onSuccess: (response) => {
      setHost(response);
      setCharacterId(response?.character?.id);
    },
    onFail: setError,
  });

  const { isFetching: isFetchingCharacter } = useFetch({
    endpoint: getCharacter({ id: characterId, runId }),
    mustFetch: characterId && !character,
    onSuccess: setCharacter,
    onFail: setError,
  });

  const { fetchData: saveHost, isFetching: isSavingHost } = useFetch({
    endpoint: putHost({ id: hostId, runId }),
    onSuccess: (response) => {
      setHost(response);
      setShowResultDialog(true);
    },
    onFail: setError,
  });

  const handleStatusChange = (newStatus) => {
    setNewStatus(newStatus);
    setShowConfirmationDialog(true);
  };

  const handleConfirm = () => {
    setShowConfirmationDialog(false);
    saveHost({ id: hostId, serial: host.serial, status: newStatus });
  };

  useEffect(() => {
    setHostId(state.id);
  }, [state]);

  const tasks = useMemo(() => getTasks({ locales }), [locales]);

  return !host || isFetchingHost || isFetchingCharacter || isSavingHost ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_CONTAINER_SX}>
      <Typography id="host-header" component="h1" sx={PAGE_TITLE_SX}>
        {host.serial}
      </Typography>
      <Grid container id="maintenance-page">
        <Grid xs={12} smd={4} md={3}>
          <DataBox locales={locales} character={character} host={host} />
        </Grid>
        <Grid xs={12} smd={1} />
        <Grid xs={12} smd={7} md={8} sx={{ mt: { xs: 4, smd: 0 } }}>
          <Box display="flex" flexDirection="column" gap={6}>
            <Box
              id="maintenance-page-controls"
              display="flex"
              alignItems="center"
              flexDirection={{ xs: "column", md: "row" }}
              justifyContent="center"
              gap={4}
            >
              <DiagnosticsChecks tasks={tasks} />
              {isTrans ? null : (
                <DiagnosticsButtons
                  locales={locales}
                  status={host.status}
                  handleStatusChange={handleStatusChange}
                />
              )}
            </Box>
            <HostGraph
              imageAltText={locales["maintenance.hostScan"]}
              status={host.status}
            />
          </Box>
        </Grid>
      </Grid>
      <ConfirmationDialog
        locales={locales}
        onClose={() => setShowConfirmationDialog(false)}
        onConfirm={handleConfirm}
        newStatus={newStatus}
        show={showConfirmationDialog}
      />
      <ResultDialog
        locales={locales}
        newStatus={newStatus}
        onClose={() => setShowResultDialog(false)}
        show={showResultDialog}
      />
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
